import type { Range } from '@attain-apps/range'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

/**
 * Conveys information about something that is or was running, including when it
 * ran and/or completed, as well as its {@link Execution.Status}
 */
export interface Execution {
  /**
   * Entire time period over which this {@link Execution} occurred
   */
  readonly timeframe: Range<Date>
  /**
   * Current {@link Status} of this {@link Step}
   *
   * `undefined` means the execution has not started
   */
  readonly status?: Execution.Status
  /**
   * Provides additional information in relation to the {@link status}, such
   * as an error message
   */
  readonly detail?: string
}
export namespace Execution {
  /**
   * Indicates the current state of an {@link Execution}, such as
   * {@link Status.InProgress} or {@link Status.Failed}
   */
  // NOTE: we use single character emojis to minimize storage when stored in
  // DynamoDB
  export enum Status {
    /**
     * {@link Execution} completed successfully
     */
    Succeeded = '✅',
    /**
     * {@link Execution} encountered an error and has terminated
     */
    Failed = '❌',
    /**
     * {@link Execution} is running without issue
     */
    InProgress = '🔄',
    /**
     * {@link Execution} is still running, but once
     * it is finished, the next step will not be executed
     */
    Stopping = '⚠️',
  }
  // Needs to be before namespace is defined
  const STATUSES: ReadonlySet<Status> = new Set(
    Object.values(Status) as Status[],
  )
  export namespace Status {
    export const ALL: readonly Status[] = Array.from(STATUSES)
    export const isStatus = TypeChecker.Factory.oneOf(STATUSES)
    export const assertIsStatus: TypeAsserter<Status> =
      TypeAsserter.Factory.from(isStatus)
  }

  /**
   * Information about how an {@link Execution} ended
   */
  export interface Completion extends Pick<Execution, 'status' | 'detail'> {
    readonly status: Execution.Status.Succeeded | Execution.Status.Failed
  }
}
